import { Disclosure, Transition } from '@headlessui/react';
import { ReportFormat, ReportType } from 'compliance-api';
import dayjs from 'dayjs';
import omit from 'lodash/omit';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { reportTypesOptions } from '@/modules/common/components/ReportTypeSelect.tsx';
import { Filters } from '@/modules/admin-dashboard/pages/OnDemandReports.tsx';
import { CalendarInput, CalendarInputProps, Select } from 'ui';
import { fixNameEnum } from 'ui/utils';

interface ReportsFiltersProps {
  filters: Filters;
  onFilterChange: Dispatch<SetStateAction<Filters>>;
}

const formatOptions = Object.values(ReportFormat).map(format => ({
  value: format,
  label: fixNameEnum(format).toUpperCase(),
}));

const OnDemandReportsFilters = ({ filters, onFilterChange }: ReportsFiltersProps) => {
  const handleChangeDateRange: CalendarInputProps['rangeOnChange'] = dates => {
    if (dates) {
      const start_date = dayjs(dates[0]).format('YYYY-MM-DD');
      const end_date = dayjs(dates[1]).format('YYYY-MM-DD');
      onFilterChange(filters =>
        start_date && end_date
          ? { ...filters, dateRange: [start_date, end_date] }
          : omit(filters, 'dateRange'),
      );
      return;
    }
    onFilterChange(filters => omit(filters, 'dateRange'));
  };

  const handleChangeFormat = useCallback(
    (option: ReportFormat | null) => {
      onFilterChange(filters =>
        option ? { ...filters, filter_by_format: option } : omit(filters, 'filter_by_format'),
      );
    },
    [onFilterChange],
  );

  const handleChangeType = useCallback(
    (option: ReportType | null) => {
      onFilterChange(filters =>
        option ? { ...filters, filter_by_type: option } : omit(filters, 'filter_by_type'),
      );
    },
    [onFilterChange],
  );

  return (
    <Transition
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
      className="flex justify-end pb-2"
    >
      <Disclosure.Panel static>
        <div className="flex gap-4 justify-end items-end flex-wrap w-fit">
          <div className="min-w-[250px] flex-1">
            <CalendarInput
              range
              rangeOnChange={handleChangeDateRange}
              rangeValue={
                filters.dateRange
                  ? [dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])]
                  : undefined
              }
              className="[&>div]:pb-3"
              disabledDate={date => dayjs().isBefore(date)}
            />
          </div>
          <div className="min-w-[150px] flex-1">
            <Select
              isClearable
              value={filters.filter_by_format}
              options={formatOptions}
              onChange={handleChangeFormat}
              placeholder="Format"
            />
          </div>
          <div className="min-w-[200px] flex-1">
            <Select
              isClearable
              value={filters.filter_by_type}
              options={reportTypesOptions}
              onChange={handleChangeType}
              placeholder="Type"
            />
          </div>
        </div>
      </Disclosure.Panel>
    </Transition>
  );
};

export default OnDemandReportsFilters;
